import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import AddAlertIcon from '@mui/icons-material/AddAlert';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { formatDateInAppTimeZone, formatDateTimeInAppTimeZone } from '../utils/dateTime';

// Empty string for same-origin API calls (nginx proxies /api/)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';

const PriceAlerts = ({ initialTicker = '' }) => {
  const theme = useTheme();
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const runtimeEnv = window.__RUNTIME_ENV__ || {};
  const authAudience = String(runtimeEnv.AUTH0_AUDIENCE || process.env.REACT_APP_AUTH0_AUDIENCE || '').trim();
  const userId = isAuthenticated && user?.sub ? user.sub : 'anonymous';
  const userEmail = isAuthenticated && user?.email ? String(user.email).trim().toLowerCase() : '';
  const userDisplayName = isAuthenticated && user?.name ? String(user.name).trim() : '';
  const [authToken, setAuthToken] = useState('');

  const getApiAccessToken = useCallback(async () => {
    const withAudience = {
      scope: 'openid profile email',
      ...(authAudience ? { audience: authAudience } : {}),
    };

    try {
      return String((await getAccessTokenSilently({ authorizationParams: withAudience })) || '');
    } catch (firstError) {
      if (!authAudience) {
        throw firstError;
      }

      // Fallback for environments where audience is not fully wired yet.
      const withoutAudience = { scope: 'openid profile email' };
      return String((await getAccessTokenSilently({ authorizationParams: withoutAudience })) || '');
    }
  }, [authAudience, getAccessTokenSilently]);

  useEffect(() => {
    let active = true;

    const loadToken = async () => {
      if (!isAuthenticated) {
        if (active) setAuthToken('');
        return;
      }

      try {
        const token = await getApiAccessToken();
        if (active) {
          setAuthToken(String(token || ''));
        }
      } catch (error) {
        if (active) {
          setAuthToken('');
        }
      }
    };

    loadToken();

    return () => {
      active = false;
    };
  }, [isAuthenticated, getApiAccessToken]);

  const buildAuthHeaders = useCallback(async () => {
    const headers = {
      'X-User-Id': userId,
      ...(userEmail ? { 'X-User-Email': userEmail } : {}),
      ...(userDisplayName ? { 'X-User-Name': userDisplayName } : {}),
    };

    if (!isAuthenticated) {
      return headers;
    }

    let token = authToken;
    if (!token) {
      token = await getApiAccessToken();
      if (token) {
        setAuthToken(token);
      }
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }, [authToken, getApiAccessToken, isAuthenticated, userDisplayName, userEmail, userId]);

  const fetchWithAuthRetry = useCallback(async (url, init = {}) => {
    const runRequest = async (preferFresh = false) => {
      const requestHeaders = await buildAuthHeaders();
      if (preferFresh && isAuthenticated) {
        setAuthToken('');
      }

      return fetch(url, {
        ...init,
        headers: {
          ...requestHeaders,
          ...(init.headers || {}),
        },
      });
    };

    let response = await runRequest(false);
    if (response.status === 401 && isAuthenticated) {
      const refreshedHeaders = await buildAuthHeaders();
      response = await fetch(url, {
        ...init,
        headers: {
          ...refreshedHeaders,
          ...(init.headers || {}),
        },
      });
    }

    return response;
  }, [buildAuthHeaders, isAuthenticated]);

  const [alerts,         setAlerts]         = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState(null);
  const [prices,         setPrices]         = useState({});
  const [checkingPrices, setCheckingPrices] = useState(false);

  // Form state
  const [formTicker, setFormTicker] = useState(initialTicker);
  const [formPrice,  setFormPrice]  = useState('');
  const [formType,   setFormType]   = useState('above');
  const [formNote,   setFormNote]   = useState('');
  const [saving,     setSaving]     = useState(false);

  const loadAlerts = useCallback(async () => {
    if (!isAuthenticated) {
      setAlerts([]);
      setError('Please sign in to view your price alerts.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res  = await fetchWithAuthRetry(`${API_BASE_URL}/api/alerts`, {
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAlerts(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load alerts: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuthRetry, isAuthenticated]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  useEffect(() => {
    setFormTicker(String(initialTicker || '').trim().toUpperCase());
  }, [initialTicker]);

  const checkPrices = useCallback(async () => {
    const activeAlerts = alerts.filter((a) => !a.triggered);
    const tickers = [...new Set(activeAlerts.map((a) => a.ticker))];
    if (!tickers.length) return;

    setCheckingPrices(true);
    const newPrices = {};

    await Promise.allSettled(
      tickers.map(async (ticker) => {
        try {
          const res  = await fetch(`${API_BASE_URL}/api/stock-price/${ticker}`);
          const data = await res.json();
          if (data.price) newPrices[ticker] = data.price;
        } catch {
          // ignore per-ticker failures
        }
      })
    );

    setPrices((prev) => ({ ...prev, ...newPrices }));

    // Auto-trigger alerts whose condition is now met
    const triggerPromises = activeAlerts
      .filter((alert) => {
        const price = newPrices[alert.ticker];
        if (price == null) return false;
        const tp = parseFloat(alert.target_price);
        return alert.alert_type === 'above' ? price >= tp : price <= tp;
      })
      .map((alert) =>
        (async () => {
          await fetchWithAuthRetry(`${API_BASE_URL}/api/alerts/${alert.id}/trigger`, {
            method: 'PATCH',
          });
        })().catch(() => {})
      );

    if (triggerPromises.length > 0) {
      await Promise.allSettled(triggerPromises);
      await loadAlerts();
    }

    setCheckingPrices(false);
  }, [alerts, fetchWithAuthRetry, loadAlerts]);

  const handleAdd = async () => {
    const cleanTicker = formTicker.trim().toUpperCase();
    if (!cleanTicker || !formPrice) return;
    setSaving(true);
    try {
      const headers = await buildAuthHeaders();
      const res = await fetchWithAuthRetry(`${API_BASE_URL}/api/alerts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          ticker:      cleanTicker,
          targetPrice: parseFloat(formPrice),
          alertType:   formType,
          note:        formNote.trim(),
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Add failed');
      }
      setFormTicker('');
      setFormPrice('');
      setFormNote('');
      setFormTicker(String(initialTicker || '').trim().toUpperCase());
      await loadAlerts();
    } catch (err) {
      setError('Failed to add alert: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const headers = await buildAuthHeaders();
      const res = await fetchWithAuthRetry(`${API_BASE_URL}/api/alerts/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Delete failed');
      }
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      setError('Failed to delete alert: ' + err.message);
    }
  };

  const activeAlerts    = alerts.filter((a) => !a.triggered);
  const triggeredAlerts = alerts.filter((a) =>  a.triggered);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <NotificationsActiveIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          Price Alerts
        </Typography>
        {activeAlerts.length > 0 && (
          <Chip
            label={`${activeAlerts.length} active`}
            color="primary"
            size="small"
            sx={{ ml: 0.5 }}
          />
        )}
        {triggeredAlerts.length > 0 && (
          <Chip
            label={`${triggeredAlerts.length} triggered`}
            color="success"
            size="small"
          />
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Set price targets for any ticker. Click <strong>Check Current Prices</strong> to
        evaluate alerts against live prices.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Add alert form */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          New Alert
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <TextField
            size="small"
            label="Ticker"
            value={formTicker}
            onChange={(e) => setFormTicker(e.target.value.toUpperCase())}
            sx={{ width: 100 }}
            inputProps={{ maxLength: 10 }}
          />
          <FormControl size="small" sx={{ width: 110 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={formType}
              label="Type"
              onChange={(e) => setFormType(e.target.value)}
            >
              <MenuItem value="above">Above ▲</MenuItem>
              <MenuItem value="below">Below ▼</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Target Price ($)"
            type="number"
            value={formPrice}
            onChange={(e) => setFormPrice(e.target.value)}
            sx={{ width: 140 }}
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            size="small"
            label="Note (optional)"
            value={formNote}
            onChange={(e) => setFormNote(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 160 }}
            inputProps={{ maxLength: 500 }}
          />
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <AddAlertIcon />}
            onClick={handleAdd}
            disabled={!formTicker.trim() || !formPrice || saving}
          >
            Add Alert
          </Button>
        </Box>
      </Paper>

      {/* Check prices action */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={checkingPrices ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={checkPrices}
          disabled={checkingPrices || activeAlerts.length === 0}
        >
          Check Current Prices
        </Button>
      </Box>

      {/* Alerts list */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : alerts.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }}
        >
          <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            No alerts set. Add your first price alert above.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Active alerts */}
          {activeAlerts.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Active ({activeAlerts.length})
              </Typography>
              <List disablePadding>
                {activeAlerts.map((alert, idx) => {
                  const currentPrice = prices[alert.ticker];
                  const tp           = parseFloat(alert.target_price);
                  const pctAway =
                    currentPrice != null
                      ? ((currentPrice - tp) / tp) * 100
                      : null;

                  return (
                    <React.Fragment key={alert.id}>
                      {idx > 0 && <Divider />}
                      <ListItem
                        sx={{ px: 0 }}
                        secondaryAction={
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(alert.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display:    'flex',
                                gap:        1,
                                alignItems: 'center',
                                flexWrap:   'wrap',
                              }}
                            >
                              <Typography
                                fontWeight={700}
                                sx={{ color: theme.palette.primary.main }}
                              >
                                {alert.ticker}
                              </Typography>
                              <Chip
                                label={`${alert.alert_type === 'above' ? '▲' : '▼'} $${parseFloat(alert.target_price).toFixed(2)}`}
                                size="small"
                                color={alert.alert_type === 'above' ? 'success' : 'error'}
                                variant="outlined"
                              />
                              {currentPrice != null && (
                                <Chip
                                  label={`Now $${currentPrice.toFixed(2)}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              {pctAway != null && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color:
                                      Math.abs(pctAway) < 3
                                        ? theme.palette.warning.main
                                        : 'text.secondary',
                                    fontWeight: Math.abs(pctAway) < 3 ? 700 : 400,
                                  }}
                                >
                                  {pctAway > 0 ? '+' : ''}
                                  {pctAway.toFixed(1)}% from target
                                </Typography>
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {alert.note
                                ? alert.note
                                : `Set ${formatDateInAppTimeZone(alert.created_at)}`}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              </List>
            </Box>
          )}

          {/* Triggered alerts */}
          {triggeredAlerts.length > 0 && (
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <CheckCircleIcon fontSize="small" color="success" />
                Triggered ({triggeredAlerts.length})
              </Typography>
              <List disablePadding>
                {triggeredAlerts.map((alert, idx) => (
                  <React.Fragment key={alert.id}>
                    {idx > 0 && <Divider />}
                    <ListItem
                      sx={{ px: 0, opacity: 0.65 }}
                      secondaryAction={
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(alert.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display:    'flex',
                              gap:        1,
                              alignItems: 'center',
                              flexWrap:   'wrap',
                            }}
                          >
                            <Typography fontWeight={700}>{alert.ticker}</Typography>
                            <Chip
                              label={`${alert.alert_type === 'above' ? '▲' : '▼'} $${parseFloat(alert.target_price).toFixed(2)}`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip label="Triggered" size="small" color="success" />
                          </Box>
                        }
                        secondary={
                          alert.triggered_at
                            ? `Triggered: ${formatDateTimeInAppTimeZone(alert.triggered_at)}`
                            : ''
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PriceAlerts;
